import Slider from "@mui/material/Slider";
import Stack from '@mui/material/Stack';
import TextField from "@mui/material/TextField/TextField";
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InformativeComponent from "../../components/utilities/InformativeComponent";
import { SimulatorContext, ISimulatorContext } from "../../context/SimulatorProvider";
import { StepperContext } from '../../context/StepperProvider';
import { VehicleContext, IVehicleContext } from '../../context/VehicleProvider';
import AutocompleteVirtualized from './components/AutocompleteVirtualized';
import VehicleOptionsDialog from './components/VehicleOptionsDialog';
import './VehicleProfile.css';

function VehicleProfile() {
    const firstRender = useRef(true)
    const { t } = useTranslation()
    const { vehicle, basicConnectors } = useContext(VehicleContext) as IVehicleContext
    const { setPreviousDisabled, setNextDisabled } = useContext(StepperContext)
    const { setUserBatteryLevel, initialBatteryLevel } = useContext(SimulatorContext) as ISimulatorContext
    const [batteryLevel, setBatteryLevel] = useState<number | string>(initialBatteryLevel ?? 20)
    const sliderMarks = [
        {
            value: 0,
            label: "0 %"
        },
        {
            value: 80,
            label: "80 %"
        }
    ]

    useEffect(() => {
        if (firstRender.current) {
            setPreviousDisabled(false)
            firstRender.current = false
        }

        if (typeof vehicle === 'undefined') {
            setNextDisabled(true)
        }
    })



    const updateBatteryLevel = (value: number | string) => {
        if (value < 0) {
            setBatteryLevel(0);
        } else if (value > 80) {
            setBatteryLevel(80);
        } else {
            setBatteryLevel(value);
        }
    }

    const handleSliderChange = (_event: Event, newValue: number | number[]) => {
        if (!isNaN(Number(newValue.toString()))) {
            updateBatteryLevel(Number(newValue));
        }
    };

    const handleBatteryLevelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(event.target.value);

        if (!isNaN(newValue)) {
            updateBatteryLevel(newValue);
        }
    };

    const handleBatteryLevelChangeCommited = (_event: Event | React.SyntheticEvent<Element, Event>, _value: number | number[]) => {
        setUserBatteryLevel(Number(batteryLevel))
    }

    return (
        <>
            <Stack textAlign="center" marginRight={2} marginLeft={2} >
                <div className='vehicle-profile-title'>
                    <Typography variant="body1">{t("vehicle-profile.body1")}</Typography>
                </div>
                <AutocompleteVirtualized />
                <VehicleOptionsDialog />
                {(typeof vehicle !== 'undefined' && basicConnectors) &&
                    <div className="battery-level-container">
                        <Typography variant="body1">
                            {t("simulation-menu.slider-label")}
                        </Typography>
                        <div className='slider-container'>
                            <Slider
                                value={(Number(batteryLevel))}
                                onChange={handleSliderChange}
                                onChangeCommitted={handleBatteryLevelChangeCommited}
                                aria-labelledby="battery-level-slider"
                                min={0}
                                max={80}
                                marks={sliderMarks}
                            />
                            <TextField
                                className="custom-input-small"
                                value={batteryLevel}
                                variant="outlined"
                                size='small'
                                onChange={handleBatteryLevelChange}
                                aria-label="Battery level"
                                inputProps={{
                                    step: 1,
                                    min: 0,
                                    max: 80,
                                    type: 'number',
                                    'aria-labelledby': 'input-slider',
                                }}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (<Typography variant="subtitle2">%</Typography>)
                                }}
                                required />
                        </div>
                        <InformativeComponent icon="alert" color="#285AFF" content={t('simulation-menu.slider-info')} />
                    </div>}
            </Stack>
        </>
    );
}

export default VehicleProfile;